import React, { useEffect, useState } from 'react';

import { Modal, Radio, Space, Tag, Typography, message } from 'antd';
import { useTranslation } from 'react-i18next';

import { ApiErrorData } from 'models/ApiResponse';
import { EnrollmentStatus } from 'models/Training';

import { LeadService } from 'services/LeadService';

import appI18n from '../../../../config/i18n';

const ALL_STATUSES: EnrollmentStatus[] = ['enrolled', 'attended', 'no_show'];

const STATUS_COLOR: Record<EnrollmentStatus, string> = {
  enrolled: 'default',
  attended: 'green',
  no_show: 'red',
};

interface Props {
  isOpen: boolean;
  trainingId: number;
  enrollmentIds: number[];
  handleModalCancel: () => void;
  onUpdated: (enrollmentIds: number[], newStatus: EnrollmentStatus) => void;
}

function BulkUpdateEnrollmentStatusModal({
  isOpen,
  trainingId,
  enrollmentIds,
  handleModalCancel,
  onUpdated,
}: Props) {
  const { t } = useTranslation(undefined, { i18n: appI18n });
  const [messageApi, contextHolder] = message.useMessage();
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [selectedStatus, setSelectedStatus] = useState<
    EnrollmentStatus | undefined
  >(undefined);

  useEffect(() => {
    if (isOpen) {
      setSelectedStatus(undefined);
    }
  }, [isOpen]);

  const handleSubmit = async () => {
    if (!selectedStatus) return;
    setSubmitting(true);
    const response = await LeadService.bulkUpdateEnrollments(trainingId, {
      enrollmentIds,
      status: selectedStatus,
    });
    setSubmitting(false);

    if (response.success) {
      onUpdated(enrollmentIds, selectedStatus);
      handleModalCancel();
    } else {
      const error = response.data as ApiErrorData;
      messageApi.error(
        (error.message as string) ||
          t('scenes.trainings.detail.bulk_status.errors.update_failed'),
        3.5
      );
    }
  };

  return (
    <Modal
      title={t('scenes.trainings.detail.bulk_status.title')}
      open={isOpen}
      onCancel={handleModalCancel}
      onOk={handleSubmit}
      okText={t('scenes.trainings.detail.bulk_status.ok', {
        count: enrollmentIds.length,
      })}
      cancelText={t('scenes.trainings.detail.bulk_status.cancel')}
      okButtonProps={{ disabled: !selectedStatus, loading: submitting }}
      destroyOnClose
      width={460}
    >
      {contextHolder}
      <Typography.Paragraph style={{ marginBottom: 12, fontSize: 13 }}>
        {t('scenes.trainings.detail.bulk_status.description', {
          count: enrollmentIds.length,
        })}
      </Typography.Paragraph>
      <Typography.Text
        type="secondary"
        style={{ display: 'block', fontSize: 12, marginBottom: 14 }}
      >
        {t('scenes.trainings.detail.bulk_status.idempotent_note')}
      </Typography.Text>
      <Typography.Text
        style={{ display: 'block', fontSize: 13, marginBottom: 10 }}
      >
        {t('scenes.trainings.detail.bulk_status.select_label')}
      </Typography.Text>
      <Radio.Group
        value={selectedStatus}
        onChange={(e) => setSelectedStatus(e.target.value as EnrollmentStatus)}
        style={{ width: '100%' }}
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          {ALL_STATUSES.map((s) => (
            <Radio key={s} value={s} style={{ width: '100%' }}>
              <Tag color={STATUS_COLOR[s]} style={{ marginRight: 6 }}>
                {t(`scenes.trainings.detail.enrollment_statuses.${s}`)}
              </Tag>
            </Radio>
          ))}
        </Space>
      </Radio.Group>
    </Modal>
  );
}

export default BulkUpdateEnrollmentStatusModal;
